// src/components/CaseStudyToc.jsx
import { useEffect, useRef, useState } from 'react';

// Aligné sur scroll-padding-top (global.css) : la ligne sous laquelle
// une ancre cliquée vient se placer, donc aussi la ligne de détection.
const ACTIVE_LINE = 110;

// Sélecteur des blocs de contenu dont la largeur peut varier (texte étroit,
// galerie plus large...). Le sommaire se centre dans l'espace laissé libre
// à gauche plutôt que de coller au contenu, pour que le contenu reste
// toujours vraiment centré.
const CONTENT_SELECTOR = '.case-study, .research-gallery';
const TOC_MIN_GUTTER = 32; // espace minimum requis (marge des deux côtés du sommaire)
const TOC_FALLBACK_LEFT = 32;

function flatten(sections) {
    return sections.flatMap((section) => [section, ...(section.children ?? [])]);
}

export default function CaseStudyToc({ sections }) {
    const flatSections = flatten(sections);
    const [activeId, setActiveId] = useState(flatSections[0]?.id);
    const [visible, setVisible] = useState(false);
    const [mobileVisible, setMobileVisible] = useState(false);
    const [tocLeft, setTocLeft] = useState(null);
    const [open, setOpen] = useState(false);
    const navRef = useRef(null);

    useEffect(() => {
        const elements = flatSections
            .map((section) => document.getElementById(section.id))
            .filter(Boolean);
        const contentEls = Array.from(document.querySelectorAll(CONTENT_SELECTOR));

        let ticking = false;

        const update = () => {
            // Le sommaire n'apparaît qu'une fois la première section vraiment
            // arrivée près du haut de l'écran, pour ne pas chevaucher un bloc
            // d'en-tête encore largement visible (maquette, hero).
            const firstEl = elements[0];
            const hasReachedContent =
                Boolean(firstEl) && firstEl.getBoundingClientRect().top < window.innerHeight * 0.4;

            let current = elements[0]?.id;
            for (const el of elements) {
                if (el.getBoundingClientRect().top <= ACTIVE_LINE) current = el.id;
            }

            // En bas d'une page courte, la dernière section peut ne jamais franchir
            // ACTIVE_LINE faute de place pour scroller davantage : on force alors
            // la dernière section à s'allumer plutôt que de rester bloqué plus haut.
            const atBottom =
                window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            if (atBottom && elements.length > 0) current = elements[elements.length - 1].id;

            setActiveId(current);

            // Suit le bord gauche du bloc de contenu actuellement au centre de l'écran,
            // au lieu d'imposer une largeur de sommaire fixe qui décale le contenu.
            // Le contenu reste toujours vraiment centré ; sur les largeurs trop
            // étroites pour loger les deux côte à côte, le sommaire s'efface plutôt
            // que de chevaucher (impossible de créer de la place qui n'existe pas).
            const viewportMid = window.innerHeight / 2;
            let currentContent = contentEls[0];
            for (const el of contentEls) {
                if (el.getBoundingClientRect().top <= viewportMid) currentContent = el;
            }

            let fits = true;
            if (currentContent && navRef.current) {
                const contentLeft = currentContent.getBoundingClientRect().left;
                const width = navRef.current.offsetWidth;
                const gutter = contentLeft - width;
                fits = gutter >= TOC_MIN_GUTTER;
                setTocLeft(Math.max(16, gutter / 2));
            }

            setVisible(hasReachedContent && fits);
            setMobileVisible(hasReachedContent);
            ticking = false;
        };

        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections]);

    // Structure commune aux deux sommaires (flottant desktop et panneau mobile) :
    // titres de groupe en gras + sous-items en retrait, seule la présentation
    // autour (colonne fixe vs. panneau flottant) change entre les deux.
    const renderSections = (onNavigate) => (
        <ul>
            {sections.map((group) => (
                <li key={group.id} className="toc-group">
                    <a
                        href={`#${group.id}`}
                        className={activeId === group.id ? 'active' : ''}
                        onClick={onNavigate}
                    >
                        {group.label}
                    </a>

                    {group.children?.length > 0 && (
                        <ul className="toc-subitems">
                            {group.children.map((child, i) => {
                                // Tant qu'on est encore sur le titre du groupe (avant d'avoir
                                // atteint son premier sous-titre), le premier enfant s'allume
                                // aussi : visuellement on est déjà dans cette sous-section.
                                const isActive =
                                    activeId === child.id || (i === 0 && activeId === group.id);
                                return (
                                    <li key={child.id}>
                                        <a
                                            href={`#${child.id}`}
                                            className={isActive ? 'active' : ''}
                                            onClick={onNavigate}
                                        >
                                            {child.label}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </li>
            ))}
        </ul>
    );

    return (
        <>
            <nav
                ref={navRef}
                className={`case-study-toc ${visible ? 'is-visible' : ''}`}
                style={{ left: tocLeft ?? TOC_FALLBACK_LEFT }}
                aria-label="Sommaire"
            >
                {renderSections()}
            </nav>

            {/* Sommaire tactile : le sommaire flottant ci-dessus s'efface faute de
                place en dessous de 1280px (tablette, mobile). Un bouton flottant
                indépendant de la nav du site (comme "retour en haut") évite tout
                chevauchement avec elle quand elle se cache au scroll. */}
            {open && <div className="toc-fab-backdrop" onClick={() => setOpen(false)} />}

            <div className={`toc-fab-wrapper ${mobileVisible ? 'is-visible' : ''}`}>
                {open && (
                    <nav className="toc-fab-panel" aria-label="Sommaire">
                        {renderSections(() => setOpen(false))}
                    </nav>
                )}

                <button
                    type="button"
                    className="toc-fab"
                    onClick={() => setOpen((o) => !o)}
                    aria-label={open ? 'Fermer le sommaire' : 'Ouvrir le sommaire'}
                    aria-expanded={open}
                >
                    {open ? (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="6" y1="6" x2="18" y2="18" />
                            <line x1="18" y1="6" x2="6" y2="18" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="7" x2="20" y2="7" />
                            <line x1="4" y1="12" x2="20" y2="12" />
                            <line x1="4" y1="17" x2="14" y2="17" />
                        </svg>
                    )}
                </button>
            </div>
        </>
    );
}
